import type { Degree, Distance, SpatialRectangle } from "@pdfking/math"
import { Center, Scale, Subtract, Total } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, HorizontalAlign, PlotFunction } from "@pdfking/renderer-api"

import { CAP_HEIGHT, PALETTE } from "#constants"

import { CreateMeasure } from "../text/CreateMeasure"
import { PlainText } from "../text/PlainText"

const MEASURE = CreateMeasure()

/** `text` as tall as `box`, in the secondary, set in it as `align` has it:
 * its box as wide as it measures and as high as its capitals stand, so what
 * it is set over can keep clear of the letters alone. A `vertical` one is
 * set along the box's height as wide as the box, turned a quarter about its
 * centre to read upwards. */
export const CaptionText = (
  box: SpatialRectangle<Distance>,
  text: string,
  align: HorizontalAlign = HorizontalAlign.CENTER,
  vertical = false
): PageShape[] => {
  if (vertical) {
    const center = Center(box)
    const along = {
      height: box.width,
      width: box.height,
      x: Subtract(center.x, Scale(box.height, 0.5)),
      y: Subtract(center.y, Scale(box.width, 0.5)),
    }
    return [
      {
        enum: PlotFunction.PLOT_GROUP,
        matrix: GroupMatrix({ origin: center, rotation: 90 as Degree }),
        shapes: CaptionText(along, text, align),
      } as PageShape,
    ]
  }
  const width = MEASURE(text, box.height)
  const x =
    align === HorizontalAlign.LEFT
      ? box.x
      : align === HorizontalAlign.RIGHT
        ? Subtract(Total(box.x, box.width), width)
        : Total(box.x, Scale(Subtract(box.width, width), 0.5))
  const text_shape = PlainText(text, x, width, box.y, box.height, PALETTE.secondary, align)
  // Set as it was, its top where it was, the box down to its capitals' foot
  const cap = Scale(box.height, CAP_HEIGHT)
  return [
    {
      ...text_shape,
      font_size: ToPixel(box.height),
      position: { height: cap, width, x, y: Total(box.y, Subtract(box.height, cap)) },
    } as PageShape,
  ]
}
